import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/hooks/useAuth.tsx';
import { routes } from '@/navigation/routes.tsx';
import {
  bottomNavigationStyles,
  hasLikesBadgeStyle,
  layoutContainerStyles,
  listItemStyle,
  listItemTextStyle,
  listStyle,
  mainBoxStyles
} from '@/components/Layout/styles.ts';
import SearchSquareShape from '@/components/common/svg/shapes/SearchSquareShape';
import UIConfig from '@/UIConfig';
import UserSquareShape from '@/components/common/svg/shapes/UserSquareShape';
import ChatShape from '@/components/common/svg/shapes/ChatShape';
import { useDialogsStore } from '@/store/useDialogsStore.ts';
import HeartSimpleShape from '@/components/common/svg/shapes/HeartSimpleShape.tsx';
import { useSummariesStore } from '@/store/useSummariesStore.ts';

const iconSize = 22;
const DARK_THEME = 'dark';
const WHITE_THEME = 'white';

const routeToTabId: Record<string, number> = {
  [routes.search]: 0,
  [routes.likes]: 1,
  [routes.messenger]: 2,
  [routes.profile]: 3
};

const Layout: React.FC = () => {
  const { t } = useTranslation();
  const { likesCount } = useSummariesStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { hasUnreadEvents, setHasUnreadEvents } = useDialogsStore();

  const [activeTab, setActiveTab] = useState(
    routeToTabId[location.pathname] || 0
  );

  const theme = useMemo(
    () => (activeTab === 0 ? DARK_THEME : WHITE_THEME),
    [activeTab]
  );

  const iconColors = {
    active: theme === DARK_THEME ? '#fff' : '#000',
    inactive:
      theme === DARK_THEME
        ? UIConfig.TabBar.Icon.Background
        : 'rgb(191 184 201)'
  };

  const backgroundColor =
    theme === DARK_THEME
      ? UIConfig.TabBar.Content.background
      : UIConfig.Colors.BackgroundWhite;

  useEffect(() => {
    const activeTab = routeToTabId[location.pathname] || 0;
    setActiveTab(activeTab);
  }, [location.pathname]);

  const handleTabClick = (id: number, path: string) => {
    setActiveTab(id);
    navigate(path);
  };

  useEffect(() => {
    if (location.pathname === routes.messenger) {
      setHasUnreadEvents(false);
    }
  }, [location.pathname, setHasUnreadEvents]);

  const eventIndicatorColor = useMemo(() => {
    if (hasUnreadEvents) {
      return '#ff0000';
    }
    return activeTab === 2 ? iconColors.active : iconColors.inactive;
  }, [hasUnreadEvents, activeTab]);

  const tabs = useMemo(
    () => [
      {
        id: 0,
        Icon: (
          <SearchSquareShape
            width={iconSize}
            height={iconSize}
            fillColor={
              activeTab === 0 ? iconColors.active : iconColors.inactive
            }
          />
        ),
        text: t('search'),
        path: routes.search
      },
      {
        id: 1,
        Icon: (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <HeartSimpleShape
              width={iconSize}
              height={iconSize}
              fillColor={
                activeTab === 1 ? iconColors.active : iconColors.inactive
              }
            />
            {likesCount !== undefined && likesCount > 0 && (
              <span style={hasLikesBadgeStyle} />
            )}
          </div>
        ),
        text: t('likes'),
        path: routes.likes
      },
      {
        id: 2,
        Icon: (
          <ChatShape
            width={iconSize}
            height={iconSize}
            fillColor={
              activeTab === 2 ? iconColors.active : iconColors.inactive
            }
            secondaryColor={eventIndicatorColor}
          />
        ),
        text: t('matches'),
        path: routes.messenger
      },
      {
        id: 3,
        Icon: (
          <UserSquareShape
            width={iconSize}
            height={iconSize}
            fillColor={
              activeTab === 3 ? iconColors.active : iconColors.inactive
            }
          />
        ),
        text: t('profile'),
        path: routes.profile
      }
    ],
    [activeTab, iconColors, t, eventIndicatorColor, likesCount]
  );

  return (
    <div style={layoutContainerStyles}>
      <div style={mainBoxStyles}>
        <Outlet />
      </div>
      {isAuthenticated && (
        <div
          style={{
            ...bottomNavigationStyles,
            background: backgroundColor,
            borderTop:
              theme === WHITE_THEME ? '1px solid rgb(224 224 224)' : 'none'
          }}
        >
          <div style={listStyle}>
            {tabs.map(({ id, text, path, Icon }) => (
              <div
                key={id}
                style={listItemStyle}
                onClick={() => handleTabClick(id, path)}
              >
                {Icon}
                <span
                  style={{
                    ...listItemTextStyle,
                    color:
                      activeTab === id ? iconColors.active : iconColors.inactive
                  }}
                >
                  {text}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
